import { useState } from 'react';
import { MapPinned, MapPin, AlertCircle, Download, Package, X, Calendar, Target, Zap, Truck, Map, Layers, RefreshCw, XCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { PlanningResult, Order, Cluster } from '../types/decision';
import { logger } from '../utils/logger';
import { CHART_PALETTE } from '../config/colors';
import { Badge } from '../components/ui/badge';
import { Button } from '@/components/ui/button';

/**
 * Planning orders may carry weight/volume under either the camelCase planning
 * names or the generic Order field names, depending on the backend mapper.
 */
type PlanningOrder = Order & {
  weightKg?: number;
  volumeM3?: number;
  orderId?: string;
  orderName?: string;
};


const AssignmentRow: React.FC<{ vehicle: PlanningResult['vehicles'][number] }> = ({ vehicle }) => {
  const assignedOrders = Array.isArray(vehicle.assignedOrders) ? vehicle.assignedOrders : [];
  const ordersCount = assignedOrders.length;
  const capacityKg = typeof vehicle.capacity_kg === 'number' ? vehicle.capacity_kg : null;
  const totalWeightAssigned = assignedOrders.reduce((sum, order) => sum + (Number(order.weightKg) || 0), 0);
  const remainingKg = capacityKg != null ? Math.max(0, capacityKg - totalWeightAssigned) : null;
  const capacityUsedPct =
    capacityKg && capacityKg > 0 ? Math.min(100, (totalWeightAssigned / capacityKg) * 100) : null;

  return (
    <tr className="hover:bg-background/50 transition-colors duration-150">
      <td className="px-4 py-3">
        <span className="font-mono font-semibold text-white text-sm">
          {vehicle.vin || String(vehicle.customerVehicleName ?? '') || '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-foreground">{vehicle.type || vehicle.model || '—'}</td>
      <td className="px-4 py-3 text-right">
        <span className="text-foreground font-medium">{capacityKg?.toLocaleString() || '—'}</span>
      </td>
      <td className="px-4 py-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold">
              {ordersCount} {ordersCount === 1 ? 'order' : 'orders'}
            </span>
            {capacityKg != null && (
              <span className="text-xs text-muted-foreground">
                {remainingKg?.toLocaleString() ?? '—'} kg remaining
              </span>
            )}
          </div>
          {capacityKg != null && (
            <div className="h-2 bg-card rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  capacityUsedPct && capacityUsedPct > 90 
                    ? 'bg-destructive' 
                    : capacityUsedPct && capacityUsedPct > 70 
                    ? 'bg-warning' 
                    : 'bg-success'
                }`}
                style={{ width: `${capacityUsedPct ?? 0}%` }}
                aria-label="Capacity used"
              />
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const FuturePlanning: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planningResult, setPlanningResult] = useState<PlanningResult | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<Cluster | null>(null);
  const [, setFetchedOrders] = useState<Order[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);

  // Order fetching state
  const [rangePreset, setRangePreset] = useState<string>('today');
  const [startISO, setStartISO] = useState<string>('');
  const [endISO, setEndISO] = useState<string>('');

  // Form state
  const [targetDate, setTargetDate] = useState('');
  const [radiusKm, setRadiusKm] = useState(30);
  const [maxOrders, setMaxOrders] = useState(200);

  // Fetch orders
  const fetchOrders = async () => {
    setFetchingOrders(true);
    setError(null);
    try {
      const url = new URL(API_ENDPOINTS.orders(), window.location.origin);
      
      if (startISO) url.searchParams.set('start', startISO);
      if (endISO) url.searchParams.set('end', endISO);
      if (!startISO && !endISO && rangePreset && rangePreset !== 'custom') {
        url.searchParams.set('range', rangePreset);
      }

      logger.info('Fetching orders from:', url.toString());

      const response = await fetch(url.toString());
      const data = await response.json();

      if (!response.ok || (data.ok === false)) {
        const errorMessage = 
          data.error?.message || 
          data.error || 
          data.message || 
          `Failed to fetch orders (${response.status})`;
        throw new Error(errorMessage);
      }

      const loadedOrders = Array.isArray(data) ? data : (data.orders || []);
      setFetchedOrders(loadedOrders);
      setOrdersCount(loadedOrders.length);
      logger.info(`Fetched ${loadedOrders.length} orders`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch orders';
      setError(message);
      logger.error('Error fetching orders:', err);
    } finally {
      setFetchingOrders(false);
    }
  };

  // Run planning
  const runPlanning = async () => {
    if (!targetDate) {
      setError('Please select a target date');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedCluster(null);
    try {
      const response = await fetch(API_ENDPOINTS.planning(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetDate,
          clusterRadiusKm: radiusKm,
          maxOrders,
          range: rangePreset !== 'custom' ? rangePreset : undefined,
          start: startISO || undefined,
          end: endISO || undefined,
        })
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error?.message || data.error || data.meta?.message || 'Planning failed');
      }

      setPlanningResult(data);
      logger.info('Planning completed:', data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to run planning';
      setError(message);
      logger.error('Error running planning:', err);
    } finally {
      setLoading(false);
    }
  };

  // Download planning result
  const downloadPlanning = async () => {
    if (!planningResult) return;

    try {
      const dataToSend = {
        ...planningResult,
        ok: true
      };

      const response = await fetch(API_ENDPOINTS.planningDownload(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.error || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `planning-${targetDate || 'result'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download planning';
      setError(message);
      logger.error('Error downloading planning:', err);
    }
  };

  // Get map center from clusters

  // Render cluster map
  const renderClusterMap = () => {
    if (!planningResult?.clusters || planningResult.clusters.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No clusters to display</p>
          </div>
        </div>
      );
    }

    const clusters = planningResult.clusters.filter(c => {
      if (!c.centroid) return false;
      const lat = c.centroid.lat;
      const lon = c.centroid.lon;
      return (
        lat != null &&
        lon != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat !== 0 &&
        lon !== 0 &&
        Math.abs(lat) <= 90 &&
        Math.abs(lon) <= 180
      );
    });
    
    if (clusters.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No clusters with valid centroids</p>
          </div>
        </div>
      );
    }
    
    const lats = clusters.map(c => c.centroid.lat);
    const lons = clusters.map(c => c.centroid.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    
    const latPadding = (maxLat - minLat) * 0.1 || 0.01;
    const lonPadding = (maxLon - minLon) * 0.1 || 0.01;
    
    const bbox = `${minLon - lonPadding},${minLat - latPadding},${maxLon + lonPadding},${maxLat + latPadding}`;
    const markers = clusters.map((cluster) => {
      const lat = cluster.centroid.lat;
      const lon = cluster.centroid.lon;
      return `${lat},${lon}`;
    }).join('|');
    
    return (
      <div className="relative w-full h-full">
        <iframe
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${markers}`}
          className="w-full h-full"
          style={{ border: 'none' }}
          title="Cluster Map"
        />
        <div className="absolute top-4 left-4 bg-popover/95 backdrop-blur-sm rounded-xl p-4 border border-border shadow-xl">
          <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-400" />
            Clusters Overview
          </p>
          <div className="space-y-2">
            {clusters.slice(0, 6).map((cluster, idx) => {
              /** Categorical cluster identifiers — stable chart palette order. */
              const colors = CHART_PALETTE.slice(0, 6);
              const color = colors[idx % colors.length];
              return (
                <div key={cluster.id || idx} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: color }} />
                  <span className="text-foreground">
                    Cluster {cluster.id || idx + 1}: <span className="font-semibold text-white">{cluster.orders?.length || 0}</span> orders
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-purple-600/20 rounded-2xl border border-blue-500/30 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <MapPinned className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                Future Planning
              </h1>
              <p className="text-foreground text-sm mt-1">
                Intelligent order clustering and vehicle assignment for future delivery dates
              </p>
            </div>
          </div>
          
          {/* Stats Cards */}
          {planningResult && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-blue-400" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Orders</p>
                </div>
                <p className="text-2xl font-bold text-white">
                  {planningResult.summary?.totalOrders || planningResult.ordersCount || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Total processed</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-4 h-4 text-indigo-400" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Clusters</p>
                </div>
                <p className="text-2xl font-bold text-indigo-400">
                  {planningResult.clustersCount || planningResult.clusters?.length || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Geographic groups</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-4 h-4 text-success" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Vehicles</p>
                </div>
                <p className="text-2xl font-bold text-success">
                  {planningResult.vehicles?.length || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Assigned</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-warning" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Unassigned</p>
                </div>
                <p className="text-2xl font-bold text-warning">
                  {planningResult.summary?.unassignedOrders || planningResult.unassignedOrders?.length || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Remaining</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/20 border border-destructive/50 rounded-xl p-4 flex items-start gap-3 backdrop-blur-sm">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-destructive font-medium">{error}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={() => setError(null)}
            className="text-destructive"
            aria-label="Dismiss error"
          >
            <XCircle className="w-4 h-4" aria-hidden />
          </Button>
        </div>
      )}

      {/* Fetch Orders Section - Redesigned */}
      <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-400" />
            Order Selection
          </h3>
          {ordersCount > 0 && (
            <Badge tone="success" className="px-3 py-1">
              {ordersCount} orders ready
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Time Range</label>
            <select
              value={rangePreset}
              onChange={(e) => {
                setRangePreset(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartISO('');
                  setEndISO('');
                }
              }}
              className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_week">Last Week</option>
              <option value="last_month">Last Month</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {rangePreset === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                <input
                  type="datetime-local"
                  value={startISO}
                  onChange={(e) => setStartISO(e.target.value)}
                  className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
                <input
                  type="datetime-local"
                  value={endISO}
                  onChange={(e) => setEndISO(e.target.value)}
                  className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                />
              </div>
            </>
          )}
          <div className={`flex items-end gap-2 ${rangePreset === 'custom' ? '' : 'md:col-span-3'}`}>
            <Button
              type="button"
              variant="default"
              size="md"
              loading={fetchingOrders}
              onClick={fetchOrders}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shadow-lg shadow-blue-500/20"
            >
              <RefreshCw className="w-4 h-4" aria-hidden />
              Fetch Orders
            </Button>
          </div>
        </div>
      </div>

      {/* Planning Parameters - Redesigned */}
      <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-400" />
            Planning Configuration
          </h3>
          {planningResult && (
            <Button
              type="button"
              variant="success"
              size="sm"
              onClick={downloadPlanning}
              className="px-3 py-1.5 bg-success/20 text-success border border-success/30 hover:bg-success/30"
            >
              <Download className="w-3 h-3" aria-hidden />
              Download CSV
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Target Date *
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Filters orders by delivery date
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Map className="w-4 h-4 inline mr-1" />
              Cluster Radius (km)
            </label>
            <input
              type="number"
              min="5"
              step="1"
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Geographic grouping distance
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Package className="w-4 h-4 inline mr-1" />
              Max Orders
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={maxOrders}
              onChange={(e) => setMaxOrders(Number(e.target.value))}
              className="w-full bg-background/50 border border-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Safety limit for processing
            </p>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="default"
              size="md"
              loading={loading}
              disabled={!targetDate}
              onClick={runPlanning}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 shadow-lg shadow-indigo-500/20"
            >
              <Zap className="w-4 h-4" aria-hidden />
              Run Planning
            </Button>
          </div>
        </div>
      </div>

      {/* Map Visualization - Redesigned */}
      {planningResult && planningResult.clusters && planningResult.clusters.length > 0 && (
        <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Map className="w-5 h-5 text-blue-400" />
              Cluster Map Visualization
            </h3>
            <span className="text-xs text-muted-foreground">
              {planningResult.clusters.length} clusters displayed
            </span>
          </div>
          <div className="bg-background rounded-xl border border-border overflow-hidden shadow-2xl" style={{ height: '600px' }}>
            {renderClusterMap()}
          </div>
        </div>
      )}

      {/* Clusters Grid - Redesigned */}
      {planningResult && planningResult.clusters && planningResult.clusters.length > 0 && (
        <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-400" />
              Delivery Clusters
            </h3>
            <span className="text-xs text-muted-foreground">
              Click a cluster to view details
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {planningResult.clusters.map((cluster, idx) => {
              const isSelected = selectedCluster?.id === cluster.id;
              return (
                <div
                  key={cluster.id || idx}
                  onClick={() => setSelectedCluster(cluster)}
                  className={`group relative rounded-xl border p-5 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl ${
                    isSelected
                      ? 'bg-gradient-to-br from-indigo-500/20 via-indigo-600/10 to-transparent border-indigo-500/50 shadow-lg shadow-indigo-500/20'
                      : 'bg-background/50 border-border hover:border-indigo-500/30'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-white text-lg mb-1">
                        Cluster {cluster.id || idx + 1}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Badge tone="info" className="px-2.5 py-1">
                          {cluster.orders?.length || 0} orders
                        </Badge>
                        {cluster.assignedVehicles && cluster.assignedVehicles.length > 0 && (
                          <Badge tone="success" className="px-2.5 py-1">
                            {cluster.assignedVehicles.length} vehicles
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Weight</span>
                      <span className="text-white font-semibold">
                        {cluster.totalWeight?.toLocaleString() || '0'} kg
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Volume</span>
                      <span className="text-white font-semibold">
                        {cluster.totalVolume?.toFixed(2) || '0.00'} m³
                      </span>
                    </div>
                    {cluster.centroid && (
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {cluster.centroid.address || `${cluster.centroid.lat?.toFixed(4)}, ${cluster.centroid.lon?.toFixed(4)}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cluster Details Modal - Redesigned */}
      {selectedCluster && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedCluster(null)}>
          <div className="bg-card rounded-2xl border border-border max-w-5xl w-full max-h-screen-90 overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 backdrop-blur-sm border-b border-border p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">Cluster {selectedCluster.id} Details</h3>
                <p className="text-sm text-muted-foreground">{selectedCluster.orders?.length || 0} orders • {selectedCluster.assignedVehicles?.length || 0} vehicles</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setSelectedCluster(null)}
                className="hover:bg-secondary"
                aria-label="Close cluster details"
              >
                <X className="w-5 h-5" aria-hidden />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-background/50 rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Orders</p>
                  <p className="text-2xl font-bold text-white">{selectedCluster.orders?.length || 0}</p>
                </div>
                <div className="bg-background/50 rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Weight</p>
                  <p className="text-2xl font-bold text-blue-400">{selectedCluster.totalWeight?.toLocaleString() || '0'} kg</p>
                </div>
                <div className="bg-background/50 rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Volume</p>
                  <p className="text-2xl font-bold text-cyan-400">{selectedCluster.totalVolume?.toFixed(2) || '0.00'} m³</p>
                </div>
                <div className="bg-background/50 rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Vehicles</p>
                  <p className="text-2xl font-bold text-success">{selectedCluster.assignedVehicles?.length || 0}</p>
                </div>
              </div>

              {/* Centroid */}
              {selectedCluster.centroid && (
                <div className="bg-background/50 rounded-xl p-4 border border-border">
                  <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-400" />
                    Centroid Location
                  </p>
                  <p className="text-white">{selectedCluster.centroid.address || 'No address available'}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Coordinates: {selectedCluster.centroid.lat?.toFixed(6)}, {selectedCluster.centroid.lon?.toFixed(6)}
                  </p>
                </div>
              )}

              {/* Assigned Vehicles */}
              {selectedCluster.assignedVehicles && selectedCluster.assignedVehicles.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-success" />
                    Assigned Vehicles
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCluster.assignedVehicles.map((vehicle, idx) => (
                      <div key={idx} className="bg-background/50 rounded-xl p-4 border border-border">
                        <p className="font-mono font-semibold text-white mb-2">{vehicle.vin}</p>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>Capacity: {vehicle.capacity_kg?.toLocaleString() || '—'} kg, {vehicle.capacity_m3?.toFixed(2) || '—'} m³</p>
                          {vehicle.utilizationPct != null && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span>Utilization</span>
                                <span className="text-white font-semibold">{vehicle.utilizationPct.toFixed(1)}%</span>
                              </div>
                              <div className="h-2 bg-card rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    vehicle.utilizationPct > 90 ? 'bg-destructive' : vehicle.utilizationPct > 70 ? 'bg-warning' : 'bg-success'
                                  }`}
                                  style={{ width: `${vehicle.utilizationPct}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orders Table */}
              {selectedCluster.orders && selectedCluster.orders.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-400" />
                    Orders in Cluster
                  </p>
                  <div className="bg-background/50 rounded-xl border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-background border-b border-border">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Order ID</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Delivery Address</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Weight (kg)</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Volume (m³)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {selectedCluster.orders.map((order, idx) => (
                            <tr key={order.id || idx} className="hover:bg-background transition-colors">
                              <td className="px-4 py-3">
                                <span className="font-mono text-white">{order.name || order.id}</span>
                              </td>
                              <td className="px-4 py-3 text-foreground">{order.delivery?.address || '—'}</td>
                              <td className="px-4 py-3 text-right text-foreground">
                                {(order as PlanningOrder).weightKg?.toLocaleString() || order.weight?.toLocaleString() || '—'}
                              </td>
                              <td className="px-4 py-3 text-right text-foreground">
                                {(order as PlanningOrder).volumeM3?.toFixed(2) || order.volume?.toFixed(2) || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Assignments - Redesigned */}
      {planningResult && planningResult.vehicles && planningResult.vehicles.length > 0 && (
        <div className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <Truck className="w-5 h-5 text-success" />
            Vehicle Assignments
          </h3>
          <div className="bg-background/50 rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground uppercase">Capacity (kg)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Orders & Utilization</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {planningResult.vehicles.map((vehicle, idx) => (
                    <AssignmentRow key={vehicle.vin || idx} vehicle={vehicle} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Orders - Redesigned */}
      {planningResult && planningResult.unassignedOrders && planningResult.unassignedOrders.length > 0 && (
        <div className="bg-destructive/20 border border-destructive/50 rounded-xl p-6 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <h3 className="text-lg font-semibold text-destructive">Unassigned Orders</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            These orders could not be assigned to any vehicle due to capacity constraints. Consider adjusting vehicle capacities or splitting loads.
          </p>
          <div className="bg-destructive/10 rounded-xl border border-destructive overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-destructive/20 border-b border-destructive">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Order ID</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground uppercase">Weight (kg)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground uppercase">Volume (m³)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-destructive/20">
                  {planningResult.unassignedOrders.map((order, idx) => (
                    <tr key={(order as PlanningOrder).orderId || idx} className="hover:bg-destructive/10 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-white">{(order as PlanningOrder).orderName || (order as PlanningOrder).orderId || order.name || order.id}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground">
                        {(order as PlanningOrder).weightKg?.toLocaleString() || order.weight?.toLocaleString() || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-foreground">
                        {(order as PlanningOrder).volumeM3?.toFixed(2) || order.volume?.toFixed(2) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!planningResult && !loading && (
        <div className="bg-card/30 rounded-2xl border border-border/50 p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/10 flex items-center justify-center">
            <MapPinned className="w-10 h-10 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Ready to Plan</h3>
          <p className="text-muted-foreground text-sm mb-4">Fetch orders, configure parameters, and run planning to see cluster assignments</p>
        </div>
      )}
    </div>
  );
};

export default FuturePlanning;
